import { useCallback } from 'react';
import {
  GetEmployeeAccountsResponse,
  GetEmployeeAccountByIdResponse,
  GetVaultPasswordsResponse,
  GetEmployeeAccountAuditLogsResponse,
} from '@uniqkey-backend-partner/api-client';
import { AxiosRequestConfig } from 'axios';
import { useApiClients } from '../../contexts/APIClientsContext';
import { dataExtractor, auditLogPayloadExtractor } from '../../helpers/apiClients';
import {
  TGetOrganizationEmployeeAccountsMethod,
  TGetOrganizationEmployeeAccountVaultPasswordsMethod,
  TGetOrganizationEmployeeAccountAuditLogsMethod,
} from './interfaces';

const useEmployeeAccountsAPI = () => {
  const { employeeAccountsAPIClient } = useApiClients();

  const getOrganizationEmployeeAccounts = useCallback<TGetOrganizationEmployeeAccountsMethod>(
    (params) => {
      const {
        organizationId,
        page,
        pageLength,
        typeFilter = undefined,
        statusFilter = undefined,
        searchQuery = undefined,
        orderPropertyName = undefined,
        isDescending = undefined,
        cancelToken = undefined,
      } = params;
      return employeeAccountsAPIClient.apiV1EmployeeAccountsGet(
        organizationId,
        page,
        pageLength,
        typeFilter,
        statusFilter,
        searchQuery,
        orderPropertyName,
        isDescending,
        { cancelToken },
      ).then(dataExtractor<GetEmployeeAccountsResponse>());
    },
    [employeeAccountsAPIClient],
  );

  const getOrganizationEmployeeAccountById = useCallback(
    (
      employeeAccountId: string,
      options?: AxiosRequestConfig,
    ): Promise<GetEmployeeAccountByIdResponse> => employeeAccountsAPIClient
      .apiV1EmployeeAccountsEmployeeAccountIdGet(employeeAccountId, options)
      .then(dataExtractor<GetEmployeeAccountByIdResponse>()),
    [employeeAccountsAPIClient],
  );

  const getOrganizationEmployeeAccountVaultPasswords = useCallback<
    TGetOrganizationEmployeeAccountVaultPasswordsMethod
  >(
    (params) => {
      const {
        employeeAccountId,
        page,
        pageLength,
        searchQuery = undefined,
        ownership = undefined,
        applicationName = undefined,
        minStrength = undefined,
        maxStrength = undefined,
        orderPropertyName = undefined,
        isDescending = undefined,
        cancelToken = undefined,
      } = params;
      return employeeAccountsAPIClient.apiV1EmployeeAccountsGetVaultPasswordsGet(
        employeeAccountId,
        page,
        pageLength,
        searchQuery,
        ownership,
        applicationName,
        minStrength,
        maxStrength,
        orderPropertyName,
        isDescending,
        { cancelToken },
      ).then(dataExtractor<GetVaultPasswordsResponse>());
    },
    [employeeAccountsAPIClient],
  );

  const getOrganizationEmployeeAccountAuditLogs = useCallback<
    TGetOrganizationEmployeeAccountAuditLogsMethod
  >(
    (params) => {
      const {
        employeeAccountId,
        page,
        pageLength,
        beforeDate = undefined,
        afterDate = undefined,
        cancelToken = undefined,
      } = params;
      return employeeAccountsAPIClient
        .apiV1EmployeeAccountsGetEmployeeAccountAuditLogsGet(
          employeeAccountId,
          page,
          pageLength,
          beforeDate,
          afterDate,
          { cancelToken },
        )
        .then(dataExtractor<GetEmployeeAccountAuditLogsResponse>())
        .then((response) => ({
          ...response,
          data: response.data!.map(((el) => auditLogPayloadExtractor(el))),
        }));
    },
    [employeeAccountsAPIClient],
  );

  return ({
    getOrganizationEmployeeAccounts,
    getOrganizationEmployeeAccountById,
    getOrganizationEmployeeAccountVaultPasswords,
    getOrganizationEmployeeAccountAuditLogs,
  });
};

export default useEmployeeAccountsAPI;
